import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, LogOut } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { useGscStatus } from '@/hooks/useApi'
import { gscApi } from '@/api/client'

/**
 * Connecting the Google account itself: OAuth in, OAuth out, and the
 * reconnect banner when the refresh token dies.
 *
 * This is account-level configuration — one Google account for the whole
 * dashboard — which is why it lives in Instellingen rather than on a scan
 * page or in a dossier tab. Which *property* a dossier reads is a separate,
 * per-dossier setting on the dossier itself.
 */
export function GscConnection() {
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const queryClient = useQueryClient()
  const location = useLocation()

  const { data: status, isLoading } = useGscStatus()

  // Google redirects back here after the consent screen.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('gsc_connected') === '1') {
      toast.success('Google Search Console verbonden!')
      queryClient.invalidateQueries({ queryKey: ['gsc'] })
      window.history.replaceState({}, '', '/settings')
    }
    if (params.get('gsc_error') === '1') {
      toast.error('Verbinding mislukt', {
        description: 'Controleer de OAuth2 instellingen en probeer opnieuw.',
      })
      window.history.replaceState({}, '', '/settings')
    }
  }, [location.search, queryClient])

  const handleConnect = async () => {
    setConnecting(true)
    try {
      const { url } = await gscApi.getAuthorizeUrl()
      window.location.href = url
    } catch (err) {
      toast.error('Verbinding starten mislukt', {
        description: err instanceof Error ? err.message : 'Onbekende fout',
      })
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try {
      await gscApi.disconnect()
      queryClient.invalidateQueries({ queryKey: ['gsc'] })
      toast.success('Google account ontkoppeld')
    } catch {
      toast.error('Ontkoppelen mislukt')
    } finally {
      setDisconnecting(false)
    }
  }

  if (isLoading) return null

  if (!status?.configured) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Google Search Console</CardTitle>
          <CardDescription>Verbind je Google-account voor zoekdata en indexeringsstatus.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!status?.oauth_client_configured ? (
            <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-950/30">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" />
              <div className="space-y-1">
                <p className="text-sm font-medium">Stap 1: OAuth2 client instellen</p>
                <p className="text-xs text-muted-foreground">
                  Vul hieronder <strong>gsc_oauth_client_id</strong> en{' '}
                  <strong>gsc_oauth_client_secret</strong> in bij de SEO-instellingen.
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Aanmaken in GCP: APIs &amp; Services → Credentials → Create Credentials → OAuth
                  2.0 Client ID → Web application.
                  <br />
                  Authorized redirect URI:{' '}
                  <code className="rounded bg-muted px-1 text-xs">
                    {window.location.origin}/api/gsc/oauth/callback
                  </code>
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div>
                <p className="text-sm font-medium">Stap 2: Verbind je Google-account</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Je geeft toegang tot alle Search Console properties van je Google-account.
                </p>
              </div>
              <Button onClick={handleConnect} disabled={connecting} className="shrink-0">
                {connecting ? <Spinner size="sm" className="mr-2" /> : null}
                Verbind Google Account
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>Google Search Console</CardTitle>
            <CardDescription>
              Accountkoppeling
              {status?.method === 'oauth2' && ' · via Google Account'}
              {status?.method === 'service_account' && ' · via Service Account'}
              . De property per dossier stel je in bij het dossier zelf.
            </CardDescription>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge className="bg-green-600 text-white">Verbonden</Badge>
            {status?.method === 'oauth2' && (
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                title="Ontkoppel Google account"
              >
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {status?.reconnect_required && (
          <div className="mt-3 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/30">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
            <div className="flex-1 space-y-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-300">
                Verbinding met Google Search Console verlopen
              </p>
              <p className="text-xs text-muted-foreground">
                De toegangstoken is ingetrokken of verlopen — er wordt geen GSC-data meer
                opgehaald. Herverbind je Google-account om de monitoring te hervatten.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={connecting} size="sm" className="shrink-0">
              {connecting ? <Spinner size="sm" className="mr-2" /> : null}
              Herverbind
            </Button>
          </div>
        )}

        {status?.method === 'service_account' && status?.oauth_client_configured && (
          <div className="mt-3 flex items-center justify-between gap-4 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 dark:border-orange-800 dark:bg-orange-950/30">
            <div>
              <p className="text-sm font-medium">Verbonden via Service Account</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Verbind je Google-account voor directe toegang tot al je Search Console
                properties.
              </p>
            </div>
            <Button size="sm" onClick={handleConnect} disabled={connecting} className="shrink-0">
              {connecting ? <Spinner size="sm" className="mr-2" /> : null}
              Verbind Google Account
            </Button>
          </div>
        )}
      </CardHeader>
    </Card>
  )
}
